/**
 * Paywall Config Tab
 * Manages paywall display configuration: texts, product badges, features list, and Apple IAP product info
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Save, RefreshCw, RotateCcw, Star, Trash2, Plus, Brain } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toast } from 'sonner';
import { baseApiClient } from '@/lib/api/client';
import { systemApi } from '@/lib/api/system';

// Types (inline, matching SubscriptionSettingsTab pattern)
interface ProductBadge {
  productId: string;
  isFeatured?: boolean;
}

interface PaywallTexts {
  heroTitle: string;
  heroSubtitle?: string;
  purchaseButtonText: string;
  valueProposition?: string;
  restoreButtonText: string;
}

interface PaywallFeature {
  icon: string;
  title: string;
  description?: string;
}

interface PaywallConfig {
  version: string;
  enabled: boolean;
  productBadges: ProductBadge[];
  texts: PaywallTexts;
  features: PaywallFeature[];
}

interface SubscriptionProduct {
  id: string;
  product_id: string;
  apple_product_id: string;
  display_price?: string;
  price?: number;
  currency?: string;
  is_available?: boolean;
  name?: string;
}

// API functions
async function fetchPaywallConfig(): Promise<PaywallConfig> {
  const { data } = await baseApiClient.get<PaywallConfig>('/v1/config/paywall');
  return data;
}

async function updatePaywallConfig(config: PaywallConfig): Promise<PaywallConfig> {
  const { data } = await baseApiClient.put<PaywallConfig>('/v1/config/paywall', config);
  return data;
}

async function resetPaywallConfig(): Promise<void> {
  await baseApiClient.delete('/v1/config/paywall');
}

async function fetchProducts(): Promise<SubscriptionProduct[]> {
  try {
    const { data } = await baseApiClient.get<SubscriptionProduct[]>('/v1/subscription/admin/products');
    return data;
  } catch {
    return [];
  }
}

// Product ID display labels
const PRODUCT_LABELS: Record<string, string> = {
  monthly: '月度 (monthly)',
  quarterly: '季度 (quarterly)',
  yearly: '年度 (yearly)',
};

function configToFormState(config: PaywallConfig): PaywallConfig {
  return JSON.parse(JSON.stringify(config));
}

export function PaywallConfigTab() {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['paywall-config'],
    queryFn: fetchPaywallConfig,
  });

  const { data: products } = useQuery({
    queryKey: ['subscription-products'],
    queryFn: fetchProducts,
  });

  const [formState, setFormState] = useState<PaywallConfig | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form state when data loads
  useEffect(() => {
    if (data && !formState) {
      setFormState(configToFormState(data));
    }
  }, [data, formState]);

  const saveMutation = useMutation({
    mutationFn: updatePaywallConfig,
    onSuccess: (newData) => {
      queryClient.invalidateQueries({ queryKey: ['paywall-config'] });
      setFormState(configToFormState(newData));
      toast.success('付费墙配置已保存');
      setHasChanges(false);
    },
    onError: (err: Error) => {
      toast.error(`保存失败: ${err.message}`);
    },
  });

  const resetMutation = useMutation({
    mutationFn: resetPaywallConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paywall-config'] });
      setFormState(null);
      toast.success('已恢复默认配置');
      setHasChanges(false);
    },
    onError: (err: Error) => {
      toast.error(`重置失败: ${err.message}`);
    },
  });

  // Form change handlers
  const handleTextChange = (key: keyof PaywallTexts, value: string) => {
    if (!formState) return;
    setFormState({
      ...formState,
      texts: { ...formState.texts, [key]: value },
    });
    setHasChanges(true);
  };

  const handleBadgeChange = (index: number, updates: Partial<ProductBadge>) => {
    if (!formState) return;
    const newBadges = [...formState.productBadges];
    newBadges[index] = { ...newBadges[index], ...updates };
    setFormState({ ...formState, productBadges: newBadges });
    setHasChanges(true);
  };

  const handleFeatureChange = (index: number, updates: Partial<PaywallFeature>) => {
    if (!formState) return;
    const newFeatures = [...formState.features];
    newFeatures[index] = { ...newFeatures[index], ...updates };
    setFormState({ ...formState, features: newFeatures });
    setHasChanges(true);
  };

  const handleAddFeature = () => {
    if (!formState) return;
    setFormState({
      ...formState,
      features: [...formState.features, { icon: 'star-outline', title: '', description: '' }],
    });
    setHasChanges(true);
  };

  const handleRemoveFeature = (index: number) => {
    if (!formState) return;
    const newFeatures = formState.features.filter((_, i) => i !== index);
    setFormState({ ...formState, features: newFeatures });
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!formState) return;
    saveMutation.mutate(formState);
  };

  const handleResetToDefaults = () => {
    if (!window.confirm('确定要恢复默认配置吗？所有自定义修改将丢失。')) return;
    resetMutation.mutate();
  };

  const handleRefresh = () => {
    setFormState(null);
    setHasChanges(false);
    refetch();
  };

  // ── Smart Paywall (prompt editor) ────────────────────────────────
  const { data: guideData, refetch: refetchGuide } = useQuery({
    queryKey: ['paywall-guide'],
    queryFn: () => systemApi.paywallGuide.get(),
  });
  const [guidePrompt, setGuidePrompt] = useState('');
  const [guideHasChanges, setGuideHasChanges] = useState(false);

  useEffect(() => {
    if (guideData && !guideHasChanges) {
      setGuidePrompt(guideData.prompt);
    }
  }, [guideData, guideHasChanges]);

  const guideSaveMutation = useMutation({
    mutationFn: (prompt: string) => systemApi.paywallGuide.update({ prompt }),
    onSuccess: () => {
      toast.success('Smart Paywall prompt saved');
      setGuideHasChanges(false);
      refetchGuide();
    },
    onError: (e: Error) => toast.error(`Save failed: ${e.message}`),
  });

  const handleGuideReset = async () => {
    try {
      const defaults = await systemApi.paywallGuide.getDefaults();
      setGuidePrompt(defaults.prompt);
      setGuideHasChanges(true);
    } catch (e) {
      toast.error('Failed to load defaults');
    }
  };

  return (
    <div className="max-w-4xl">
      {/* ── Smart Paywall Section ── */}
      <div className="mb-8 rounded-lg border border-indigo-200 bg-indigo-50/30 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Brain className="w-5 h-5 text-indigo-600" />
          <h3 className="text-base font-semibold text-indigo-900">Smart Paywall</h3>
          <span className="text-xs text-indigo-500 ml-2">Agent 自主判断引导时机</span>
        </div>

        <p className="text-sm text-gray-600 mb-3">
          Agent 根据对话中积累的"社交直觉"，在用户体验到价值后自然引导付费。
          开关在 Agent 配置 → Hooks → <code className="bg-gray-100 px-1 rounded">paywall_context</code>。
        </p>

        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            引导 Prompt
            {guideData?.is_default && (
              <span className="ml-2 text-xs text-amber-600">(使用默认模板)</span>
            )}
          </label>
          <textarea
            className="w-full h-48 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono resize-y focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={guidePrompt}
            onChange={(e) => {
              setGuidePrompt(e.target.value);
              setGuideHasChanges(true);
            }}
            placeholder="引导 prompt 模板..."
          />
          {guideData?.updated_by && (
            <p className="text-xs text-gray-400 mt-1">
              v{guideData.version} · {guideData.updated_by} · {guideData.updated_at ? new Date(guideData.updated_at).toLocaleString() : ''}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={() => guideSaveMutation.mutate(guidePrompt)}
            disabled={!guideHasChanges || guideSaveMutation.isPending}
          >
            <Save className="w-4 h-4 mr-1" />
            {guideSaveMutation.isPending ? '保存中...' : '保存 Prompt'}
          </Button>
          <Button variant="outline" onClick={handleGuideReset}>
            <RotateCcw className="w-4 h-4 mr-1" />
            恢复默认
          </Button>
        </div>
      </div>

      {/* ── Paywall UI Config (existing) ── */}
      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mb-6">
        <Button variant="outline" onClick={handleRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" />
          刷新
        </Button>
        <Button
          variant="danger"
          onClick={handleResetToDefaults}
          disabled={resetMutation.isPending}
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          {resetMutation.isPending ? '重置中...' : '恢复默认'}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!hasChanges || saveMutation.isPending}
        >
          <Save className="w-4 h-4 mr-2" />
          {saveMutation.isPending ? '保存中...' : '保存修改'}
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-semibold">加载配置失败</h3>
              <p className="text-red-600 text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Form */}
      {formState && (
        <div className="space-y-6">
          {/* Section: Texts */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">文案配置</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  主标题 (heroTitle)
                </label>
                <input
                  type="text"
                  value={formState.texts.heroTitle}
                  onChange={(e) => handleTextChange('heroTitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  副标题 (heroSubtitle)
                </label>
                <input
                  type="text"
                  value={formState.texts.heroSubtitle || ''}
                  onChange={(e) => handleTextChange('heroSubtitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  购买按钮文案 (purchaseButtonText)
                </label>
                <input
                  type="text"
                  value={formState.texts.purchaseButtonText}
                  onChange={(e) => handleTextChange('purchaseButtonText', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  使用 {'{price}'} 作为价格占位符
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  价值主张 (valueProposition)
                </label>
                <input
                  type="text"
                  value={formState.texts.valueProposition || ''}
                  onChange={(e) => handleTextChange('valueProposition', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  使用 {'{dailyPrice}'} 作为日均价占位符
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  恢复购买按钮 (restoreButtonText)
                </label>
                <input
                  type="text"
                  value={formState.texts.restoreButtonText}
                  onChange={(e) => handleTextChange('restoreButtonText', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Section: Default Selected Product */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">默认选中产品</h2>
            <p className="text-sm text-gray-500 mb-4">
              用户打开付费墙时默认高亮的产品，每次只能设一个。角标文字在「产品管理」里按平台配置。
            </p>
            <div className="space-y-3">
              {formState.productBadges.map((badge, index) => (
                <div key={badge.productId} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700 flex-1">
                    {PRODUCT_LABELS[badge.productId] || badge.productId}
                  </span>

                  <button
                    type="button"
                    onClick={() => handleBadgeChange(index, { isFeatured: !badge.isFeatured })}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                      badge.isFeatured
                        ? 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200 border border-yellow-300'
                        : 'text-gray-400 bg-white hover:text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                    title={badge.isFeatured ? '取消默认选中' : '设为默认选中'}
                  >
                    <Star className="w-4 h-4" fill={badge.isFeatured ? 'currentColor' : 'none'} />
                    <span>{badge.isFeatured ? '默认选中' : '设为默认'}</span>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Section: Features */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">功能列表</h2>
            <div className="space-y-4">
              {formState.features.map((feature, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          图标名称
                        </label>
                        <input
                          type="text"
                          value={feature.icon}
                          onChange={(e) => handleFeatureChange(index, { icon: e.target.value })}
                          placeholder="chatbubbles-outline"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          标题
                        </label>
                        <input
                          type="text"
                          value={feature.title}
                          onChange={(e) => handleFeatureChange(index, { title: e.target.value })}
                          placeholder="无限 AI 对话"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(index)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors mt-4"
                      title="删除"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      描述
                    </label>
                    <input
                      type="text"
                      value={feature.description || ''}
                      onChange={(e) => handleFeatureChange(index, { description: e.target.value })}
                      placeholder="与 Dora AI 助手无限制对话..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500 text-sm"
                    />
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={handleAddFeature}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                添加功能项
              </button>
            </div>
          </div>

          {/* Section: Apple IAP Products (read-only) */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Apple IAP 产品信息</h2>
            <p className="text-sm text-gray-500 mb-4">
              以下信息由 Apple StoreKit 提供，在 App Store Connect 中管理
            </p>

            {products && products.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 px-3 font-medium text-gray-700">Product ID</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700">Apple Product ID</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700">价格</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-700">状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id || product.product_id} className="border-b border-gray-100">
                        <td className="py-2 px-3 text-gray-900 font-mono text-xs">
                          {product.product_id}
                        </td>
                        <td className="py-2 px-3 text-gray-600 font-mono text-xs">
                          {product.apple_product_id}
                        </td>
                        <td className="py-2 px-3 text-gray-600">
                          {product.display_price || (product.price ? `${product.currency || ''} ${product.price}` : '-')}
                        </td>
                        <td className="py-2 px-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            product.is_available !== false
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {product.is_available !== false ? '可用' : '不可用'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">暂无产品数据</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
